import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceOpportunityService } from '@klickdata/core/opportunity';
import { AppScope, Resource } from '@klickdata/core/resource';
import { StartResourceService } from '@klickdata/core/resource/src/start-resource.service';
import { ResourceTypes } from '@klickdata/core/resource/src/types.enum';
import {
    Filter,
    GlobalFilterProperty,
    GlobalFilterPropertyType,
    SelectFilterOption,
    StatusFilterOption,
    TableFilterComponent,
} from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { ResourceTableBaseComponent } from '@klickdata/shared-components/src/resource-table-base/resource-table-base.component';
import { ShareService } from '@klickdata/shared-components/src/share/share.service';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { BehaviorSubject, EMPTY, filter, map, Observable, switchMap, takeUntil, tap } from 'rxjs';
import moment from 'moment';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';

@Component({
    selector: 'app-dashboard-user-activity-list',
    templateUrl: './dashboard-user-activity-list.component.html',
    styleUrls: ['./dashboard-user-activity-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardUserActivityListComponent extends OnDestroyHandler implements AfterViewInit, OnInit {
    @ViewChild('tableBase') tableBase: ResourceTableBaseComponent;
    private _tableFilterItems: Filter<any>[];
    @Input() set tableFilterItems(filters: Filter<any>[]) {
        this._tableFilterItems = [...filters, new Filter('eager', ['user_collaboration', 'enrollment'])];
    }
    get tableFilterItems(): Filter<any>[] {
        return this._tableFilterItems;
    }
    @Input() user: User;
    @Input() pageSizeOptions = [25, 50, 100];
    @Input() matSortActive = 'last_action';
    @ViewChild('title', { static: true }) title: TemplateRef<any>;
    @ViewChild('img', { static: true }) img: TemplateRef<any>;
    @ViewChild('type', { static: true }) type: TemplateRef<any>;
    @ViewChild('last_action', { static: true }) last_action: TemplateRef<any>;
    @ViewChild('occasionStatus', { static: true }) occasionStatus: TemplateRef<any>;
    @ViewChild('done_items', { static: true }) done_items: TemplateRef<any>;
    @ViewChild('examination', { static: true }) examination: TemplateRef<any>;
    @ViewChild('expansionContextTemplate', { static: true }) expansionContextTemplate: TemplateRef<any>;
    @ViewChild('multipleSelectionActionsTemplate', { static: true }) multipleSelectionActionsTemplate: TemplateRef<any>;
    // @ViewChild('multipleSelectionActions', { static: true }) multipleSelectionActions: TemplateRef<any>;
    @ViewChild('tools', { static: true }) tools: TemplateRef<any>;
    public resourceStatusOptions: StatusFilterOption[];
    public resourceTypeOptions: SelectFilterOption[];
    public shareLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    GlobalFilterPropertyType = GlobalFilterPropertyType;
    public isMobile$: Observable<boolean>;
    public customer$: Observable<Customer>;
    public expandedRowId: number | null;
    GlobalFilterProperty = GlobalFilterProperty;
    columnsData: { [key: string]: TemplateRef<any> } = {};
    otherContext: { [key: string]: TemplateRef<any> } = {};
    columnsKeys: any = {
        img: { label: $localize`Image`, sortable: false },
        title: { label: $localize`Title`, sortable: true },
        type: { label: $localize`Type`, sortable: true },
        last_action: { label: $localize`Last Action`, sortable: true },
        occasionStatus: { label: $localize`Status`, sortable: true },
        done_items: { label: $localize`Progress`, sortable: true },
        examination: { label: $localize`Certificate`, sortable: false },
    };
    ResourceTypes = ResourceTypes;
    AppScope = AppScope;
    expandedFn = (resource: Resource) => (resource.opportunity_id || resource.id) === this.expandedRowId;

    constructor(
        protected downloadHelper: DownloadHelperService,
        public shareService: ShareService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected cdr: ChangeDetectorRef,
        protected resourceOpportunityService: ResourceOpportunityService,
        protected mobile: MobileService,
        protected auth: AuthService,
        protected oppoService: ResourceOpportunityService,
        protected messageService: MessageService,
        protected startResourceService: StartResourceService
    ) {
        super();
        this.resourceStatusOptions = Utils.listResourceOccasionsStatus();
        this.resourceTypeOptions = Utils.getResourceTypeOptions();
        this.isMobile$ = mobile.isMobile();
    }
    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                filter((params) => params.expand),
                takeUntil(this.destroy)
            )
            .subscribe((params) => {
                setTimeout(() => {
                    this.expandedRowId = +params.expand;
                    this.cdr.markForCheck();
                }, 500);
            });
        this.customer$ = this.auth.getCustomer();
    }
    ngAfterViewInit(): void {
        this.columnsData['img'] = this.img;
        this.columnsData['title'] = this.title;
        this.columnsData['last_action'] = this.last_action;
        this.columnsData['type'] = this.type;
        this.columnsData['occasionStatus'] = this.occasionStatus;
        this.columnsData['done_items'] = this.done_items;
        this.columnsData['examination'] = this.examination;
        this.columnsData['tools'] = this.tools;
        this.otherContext['expansionContext'] = this.expansionContextTemplate;
        this.otherContext['multipleSelectionActions'] = this.multipleSelectionActionsTemplate;
        // this.otherContext['multipleSelectionActions'] = this.multipleSelectionActions;
    }
    public getPlayBtnTxt(resource: Resource): string {
        return resource.occasionStatus === 'ongoing' ? $localize`:@@continue:Continue` : $localize`:@@play:Play`;
    }
    retake(resource: Resource) {
        this.startResourceService.takeResource(resource, this.user)?.subscribe();
    }
    public download(resource: Resource) {
        this.downloadHelper.downloadWithOptions(resource.downloads);
    }
    browse(resource: Resource) {
        this.router.navigate(['/home/dashboard/resource-details/' + resource.id]);
    }
    canExpand(resource: Resource) {
        return (
            resource.occasionStatus == 'created' ||
            resource.opportunity_id ||
            ResourceTypes.parentType(resource.type_id) === ResourceTypes.COURSE
        );
    }
    get isCompletedFilter(): boolean {
        return (
            !!this.tableFilterItems?.length && !!this.tableFilterItems.filter((fltr) => fltr.property == 'done').length
        );
    }
    isActivityTab(): boolean {
        return (
            !!this.tableFilterItems?.length &&
            !!this.tableFilterItems.filter((fltr) => fltr.property == 'userActivity').length
        );
    }
    public deleteCourseFromList(resource: Resource) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete ${resource.title}`,
                contentBody: $localize`You are about to delete ${resource.title} from being displayed in the activities. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                takeUntil(this.destroy),
                filter((res) => !!res),
                switchMap(() => this.resourceOpportunityService.destroy(resource.opportunity_id))
            )
            .subscribe({
                next: () => {
                    this.tableBase.dataSource.removeById(resource.id);
                    this.messageService.openMessage(
                        MessageSavedComponent,
                        $localize`:@@resoureHasBeenHidden:The resource has been successfully deleted from the list.`
                    );
                },
                error: () => {
                    this.messageService.openMessage(
                        MessageErrorComponent,
                        $localize`:@@someThingWentWrong:Oops! Something went wrong. Please try again later.`
                    );
                },
            });
    }
    public downloadSelected(res: Resource[]) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.DOWNLOAD,
            data: {
                data: Utils.getDownloadOptions('resource_opportunity'),
                returnValue: true,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobile.getSideNavResponseData()),
                takeUntil(this.destroy)
            )
            .subscribe((result) => {
                const ids = res.map((res) => res.opportunity_id);
                if (!Utils.isEmpty(result?.value) && !Utils.isEmpty(ids)) {
                    this.oppoService.download(ids, result.value.join()).subscribe((res) => {
                        const file = new Blob([res], { type: res.type });
                        const fileUrl = URL.createObjectURL(file);
                        const anchor = document.createElement('a');
                        anchor.href = fileUrl;
                        anchor.download = `KLMS_${moment().format('yMD_hms')}`;
                        anchor.click();
                        this.tableBase.selection.clear();
                    });
                }
            });
    }
    public play(resource: Resource): void {
        switch (resource.occasionStatus) {
            case 'not_started':
            case 'ongoing':
            case 'overdue':
            case 'failed':
            case 'done':
                this.startResourceService.takeResource(resource, this.user)?.subscribe();
                break;
            case 'cancelled':
                this.handlePlayCancelledStatusPopup(resource);
                break;
            case 'completed':
                this.handleBrowseDoneStatusPopup(resource);
                break;
            default:
                this.startResourceService.takeResource(resource, this.user)?.subscribe();
        }
    }
    handlePlayCancelledStatusPopup(resource: Resource) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'cancel',
                title: $localize`Cancelled ${ResourceTypes.label(resource.type_id, {
                    plural: false,
                    capitalize: false,
                })}`,
                contentBody: $localize`Do you want to restart this ${ResourceTypes.label(resource.type_id, {
                    plural: false,
                    capitalize: false,
                })}?`,
                positiveBtn: $localize`Start ${ResourceTypes.label(resource.type_id, {
                    plural: false,
                    capitalize: false,
                })}`,
                negativeBtn: $localize`Delete from the list`,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                takeUntil(this.destroy),
                tap(() => this.startResourceService.startResource(resource))
            )
            .subscribe();
    }
    public handleBrowseDoneStatusPopup(resource: Resource) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.RESOURCE_DONE_ACTIVITY,
            data: {
                resource: resource,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobile.getSideNavResponseData()),
                map((data) => data.value),
                takeUntil(this.destroy),
                switchMap((response) => {
                    switch (response) {
                        case 'restart':
                            return this.resourceOpportunityService
                                .getOpportunity(resource.opportunity_id)
                                .pipe(
                                    switchMap((opp) =>
                                        this.resourceOpportunityService
                                            .done(opp)
                                            .pipe(tap(() => this.startResourceService.startResource(resource)))
                                    )
                                );
                        case 'signOff':
                            return this.resourceOpportunityService.getOpportunity(resource.opportunity_id).pipe(
                                switchMap((opp) => this.resourceOpportunityService.done(opp)),
                                tap(() => this.tableBase.dataSource.refresh())
                            );
                        case 'continue':
                            return this.startResourceService.takeResource(resource, this.user);
                    }
                    return EMPTY;
                })
            )
            .subscribe();
    }

    canEdit(resource: Resource, customer: Customer) {
        return (
            !!resource.user_collaboration?.edit?.status &&
            (this.user.isAdmin() ||
                customer.authoring_allowed_types?.includes(AppScope.RESOURCES) ||
                customer.authoring_allowed_types?.includes(ResourceTypes.scopeByChildType(resource.type_id)))
        );
    }

    edit(resource: Resource) {
        if (resource.type_id === ResourceTypes.GeneralCoursePlan) {
            this.user.isAdmin()
                ? this.router.navigate(['/admin/content/courses/', resource.id])
                : this.router.navigate(['/home/manage/course-plan/', resource.id]);
        } else {
            this.user.isAdmin()
                ? this.router.navigate([`/admin/content/${ResourceTypes.playerRoute(resource.type_id)}s/`, resource.id])
                : this.router.navigate([`/home/manage/${ResourceTypes.playerRoute(resource.type_id)}/`, resource.id]);
        }
    }
    onEditCreated(resource: Resource, customer: Customer) {
        if (resource.occasionStatus == 'created' && this.canEdit(resource, customer)) {
            this.edit(resource);
        }
    }
    expansionToggled(resource: Resource) {
        if (this.canExpand(resource)) {
            const expandId = resource.opportunity_id || resource.id;
            this.expandedRowId = this.expandedRowId === expandId ? null : expandId;
        }
    }
}
